import {
  GridExcelExportOptions,
  GridRowId,
  gridFilteredSortedRowIdsSelector,
} from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { RowInput, autoTable } from 'jspdf-autotable';
import { last, sortBy } from 'lodash';
import { MutableRefObject } from 'react';

import { PDFDocumentDetails, generateSolaforcePDF } from '~/utils/generatePDF';

export const exportToPDF =
  (apiRef: MutableRefObject<GridApiPremium>) =>
  (options: GridExcelExportOptions, documentDetails: PDFDocumentDetails) => {
    const { fileName, getRowsToExport } = options;

    // Solaforce Logo
    const img = new Image();
    img.src = '/v2/img/logoBlueFooter.png';

    // Table Columns
    const visibleColumns = (apiRef.current.getVisibleColumns() ?? []).filter(
      (column) => !column.disableExport,
    );
    const columns = visibleColumns.map((column) => {
      const { field, headerName } = column;
      return { dataKey: field, header: headerName ?? field };
    });
    const columnStyles = Object.fromEntries(
      visibleColumns.map((column) => {
        const { field, minWidth } = column;

        return [
          field,
          {
            minCellWidth: minWidth,
          },
        ];
      }),
    );

    // Table Column Groups
    const getTableColumnGroups = (): RowInput[] => {
      const getGroupPath = (field: string) =>
        apiRef.current.unstable_getColumnGroupPath(field);
      const allGroupDetails =
        apiRef.current.unstable_getAllGroupDetails() ?? {};

      const getColumnGroupHeaderName = (groupId: string) => {
        const group = allGroupDetails[groupId];

        return !!group ? group.headerName ?? group.groupId : '';
      };

      const fieldGroups = visibleColumns.map(({ field }) => {
        return {
          field,
          groupIds: getGroupPath(field),
        };
      });

      const groupLevelsCount = fieldGroups.reduce((acc, { groupIds }) => {
        return groupIds.length > acc ? groupIds.length : acc;
      }, 0);

      if (groupLevelsCount < 1) {
        return [];
      }

      const groupLevelIndexes = Array(groupLevelsCount)
        .fill(' ')
        .map((_value, index) => index);

      const filledFieldGroups = fieldGroups.map((fieldInfo) => {
        const { groupIds } = fieldInfo;

        if (groupIds.length < groupLevelsCount) {
          return {
            ...fieldInfo,
            groupIds: groupIds.concat(
              Array(groupLevelsCount - groupIds.length).fill(' '),
            ),
          };
        }

        return fieldInfo;
      });

      const getLevelGroupIds = (levelIndex: number): string[] => {
        return filledFieldGroups.map(({ groupIds }) => groupIds[levelIndex]);
      };

      const getColSpans = (groupIds: string[]) => {
        const result = [];
        let counter = 1;
        for (let i = 0; i < groupIds.length; i++) {
          if (groupIds[i] === groupIds[i + 1]) {
            counter++;
          } else {
            result.push({
              content: getColumnGroupHeaderName(groupIds[i]),
              colSpan: counter,
            });
            counter = 1;
          }
        }
        return result;
      };

      return groupLevelIndexes.map(getLevelGroupIds).map(getColSpans);
    };

    const tableHead = [
      ...getTableColumnGroups(),
      visibleColumns.map((column) => {
        return { content: column.headerName ?? column.field };
      }),
    ];

    // Table Data Rows
    const getRowsData = (): GridRowId[] => {
      const filteredSortedRowIds = gridFilteredSortedRowIdsSelector(apiRef);

      const getSelectionRowIds = () => {
        const selectedRows = apiRef.current.getSelectedRows();

        if (selectedRows.size > 0) {
          const exportRowIds = getRowsToExport
            ? getRowsToExport({ apiRef })
            : [];

          return exportRowIds.length > 0
            ? exportRowIds
            : Array.from(selectedRows.keys());
        }

        return [];
      };

      const selectionRowIds = getSelectionRowIds();

      return selectionRowIds.length > 0
        ? filteredSortedRowIds.filter((rowId) =>
            selectionRowIds.includes(rowId),
          )
        : filteredSortedRowIds;
    };

    const tableBody = getRowsData().map((id) => {
      const rowData = Object.fromEntries(
        visibleColumns.map(({ field }) => {
          const { formattedValue } = apiRef.current.getCellParams(id, field);

          return [field, formattedValue];
        }),
      );

      return { rowId: id, ...rowData };
    });

    // Resolve Page size
    const resolvePageWidth = (defaultColumnWidth: number, minWidth: number) => {
      const defaultPageWidth = visibleColumns.length * defaultColumnWidth;

      const getOnlyNumberValues = (values: (number | undefined)[]) =>
        values.filter((width): width is number => typeof width === 'number');

      const getLargestValue = (values: number[], defaultValue: number) =>
        last(sortBy(values)) ?? defaultValue;

      const resolvedColumnWidths = visibleColumns.map(
        ({ width, minWidth, computedWidth }) => {
          return getLargestValue(
            getOnlyNumberValues([
              defaultColumnWidth,
              width,
              minWidth,
              computedWidth,
            ]),
            defaultColumnWidth,
          );
        },
      );

      const resolvedPageWidth = resolvedColumnWidths.reduce(
        (total: number, colWidth) => {
          const addWidth: number = colWidth ?? defaultColumnWidth;
          return total + addWidth;
        },
        0,
      );

      return getLargestValue(
        getOnlyNumberValues([minWidth, defaultPageWidth, resolvedPageWidth]),
        minWidth ?? defaultPageWidth,
      );
    };

    const resolvePageHeight = (minContentRowHeight: number) => {
      const buffer = 100;
      const tableHeadHeight = tableHead.length * (minContentRowHeight + 10);
      const tableContentHeight = tableBody.length * minContentRowHeight;

      return buffer + tableHeadHeight + tableContentHeight;
    };

    const pageSize = {
      width: resolvePageWidth(150, 900),
      height: resolvePageHeight(30),
    };

    // Construct PDF
    generateSolaforcePDF({
      fileName,
      documentDetails,
      contentSize: pageSize,
      addContent: ({ doc, margin }) => {
        autoTable(doc, {
          columns,
          head: tableHead,
          body: tableBody,
          bodyStyles: {
            cellWidth: 'wrap',
          },
          columnStyles,
          startY: margin.top,
        });

        return doc;
      },
    });
  };
